import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, RefreshCw } from "lucide-react";
import { getSelectedOrgRegime, loadTdsWorkspace, markWorkspaceStepComplete, resetRegimeSelection } from "./tdsStorage";
import { getWorkspaceStepsForRegime } from "./tdsNavConfig";
import { TdsGuideSidebar } from "./TdsUserGuide";
import { TAX_REGIMES } from "./tdsConstants";
import { TDS_START_PATH } from "./tdsNavConfig";

export default function TdsWorkspace() {
  const regime = getSelectedOrgRegime();
  const regimeLabel = TAX_REGIMES.find((r) => r.value === regime)?.label || "Tax Regime";
  const steps = useMemo(() => getWorkspaceStepsForRegime(regime), [regime]);
  const [expandedId, setExpandedId] = useState(steps[0]?.id || null);
  const completed = useMemo(() => new Set(loadTdsWorkspace().completedSteps || []), []);

  const handleOpenStep = (step) => {
    markWorkspaceStepComplete(step.id);
    setExpandedId(step.id);
  };

  return (
    <div className="tds-workspace-layout">
      <div className="tds-workspace-main">
        <section className="tds-workspace-intro">
          <div>
            <p className="tds-workspace-intro__eyebrow">Your TDS workspace</p>
            <h2 className="tds-workspace-intro__title">Complete TDS step by step</h2>
            <p className="tds-workspace-intro__text">
              Features are tailored for the <strong>{regimeLabel}</strong>. Open each section when you
              need it — nothing else is shown upfront.
            </p>
          </div>
          <Link
            to={TDS_START_PATH}
            className="tds-btn tds-btn--ghost tds-btn--sm"
            onClick={() => resetRegimeSelection()}
          >
            <RefreshCw size={14} />
            Change regime
          </Link>
        </section>

        <div className="tds-workspace-progress" aria-label="Workflow progress">
          {steps.map((step, index) => {
            const done = completed.has(step.id);
            const active = expandedId === step.id;
            const Icon = step.icon;
            return (
              <article
                key={step.id}
                className={`tds-workspace-step${active ? " tds-workspace-step--open" : ""}${done ? " tds-workspace-step--done" : ""}`}
              >
                <button
                  type="button"
                  className="tds-workspace-step__head"
                  onClick={() => setExpandedId(active ? null : step.id)}
                  aria-expanded={active}
                >
                  <span className="tds-workspace-step__num">{index + 1}</span>
                  <span className="tds-workspace-step__icon" aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  <span className="tds-workspace-step__meta">
                    <span className="tds-workspace-step__title">{step.title}</span>
                    <span className="tds-workspace-step__summary">{step.summary}</span>
                  </span>
                  <ChevronRight
                    size={18}
                    className={`tds-workspace-step__chev${active ? " tds-workspace-step__chev--open" : ""}`}
                  />
                </button>
                {active && (
                  <div className="tds-workspace-step__body">
                    <p className="tds-workspace-step__help">{step.summary}</p>
                    <Link
                      to={step.path}
                      className="tds-btn tds-btn--primary"
                      onClick={() => handleOpenStep(step)}
                    >
                      {step.actionLabel}
                    </Link>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>

      <aside className="tds-workspace-guide" aria-label="User guide">
        <TdsGuideSidebar defaultSection="tax-regime" regime={regime} />
      </aside>
    </div>
  );
}
